import { isEqual } from 'lodash-es';
import type { StoreApi } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { LAYER_STATUS } from '@/lib/db-constants';
import { buildLayerDataSources } from '@/utils/layer-sources/layer-fetcher';
import { fetchLayersByIds } from '@/utils/models/layers';
import {
  type LayerDataSourcesDataState,
  createLayerDataSourcesSlice,
} from './slices/dataSources.slice';
import { type IconMetadataState, createIconMetadataSlice } from './slices/icon-metadata.slice';
import { type JobsState, createJobsSlice } from './slices/jobs.slice';
import { type LayerFiltersState, createLayerFiltersSlice } from './slices/layerFilters.slice';
import { type LayerTablesState, createLayerTablesSlice } from './slices/layerTables.slice';
import { type LayersState, createLayersSlice } from './slices/layers.slice';
import { type MapLayerState, createMapLayerSlice } from './slices/mapLayers.slice';
import { type MapPanelsUIState, createMapPanelsUISlice } from './slices/mapPanelsUI.slice';
import { type MapsState, createMapsSlice } from './slices/maps.slice';
import { type SharedViewState, createSharedViewSlice } from './slices/sharedView.slice';
import { type SitesPanelState, createSitesPanelSlice } from './slices/sitesPanel.slice';
import { type UserState, createUserSlice } from './slices/user.slice';

// Combined state
export type GeoforgeStore = UserState &
  MapsState &
  LayersState &
  MapLayerState &
  LayerDataSourcesDataState &
  MapPanelsUIState &
  LayerTablesState &
  LayerFiltersState &
  JobsState &
  IconMetadataState &
  SharedViewState &
  SitesPanelState & {
    clearWorkspaceData: () => void;
    blockStoreLayerSubscriptions: boolean;
    setBlockStoreLayerSubscriptions: (shouldBlock: boolean) => void;
  };

export const createGeoforgeStore = (
  initState?: Partial<GeoforgeStore>
): StoreApi<GeoforgeStore> => {
  const store = createStore<GeoforgeStore>()(
    subscribeWithSelector((set, get) => ({
      ...createUserSlice(set, get),
      ...createMapsSlice(set),
      ...createLayersSlice(set),
      ...createMapLayerSlice(set),
      ...createLayerDataSourcesSlice(set, get),
      ...createMapPanelsUISlice(set),
      ...createLayerTablesSlice(set),
      ...createLayerFiltersSlice(set),
      ...createJobsSlice(set, get),
      ...createIconMetadataSlice(set, get),
      ...createSharedViewSlice(set),
      ...createSitesPanelSlice(set, get),
      blockStoreLayerSubscriptions: false,
      clearWorkspaceData: () => {
        get().clearUser();
        get().clearMaps();
        get().clearLayers();
        get().clearMapLayers();
        get().clearDataSources();
        get().clearMapPanelsUI();
        get().clearLayerTables();
        get().resetFilterState();
        get().clearJobs();
        get().clearIconMetadata();
        get().clearSharedView();
        get().clearSitesPanel();
      },
      setBlockStoreLayerSubscriptions: (shouldBlock: boolean) => {
        set({ blockStoreLayerSubscriptions: shouldBlock });
      },
      ...initState,
    }))
  );

  let isInitialLoad = true;
  let hasHydratedInitialData = false;

  store.subscribe(
    state => ({ layers: state.layers, mapLayersList: state.mapLayersList }),
    async ({ layers, mapLayersList }, { layers: prevLayers, mapLayersList: prevMapLayersList }) => {
      const { addDataSource, removeDataSource, addLayer, blockStoreLayerSubscriptions } =
        store.getState();

      if (blockStoreLayerSubscriptions) {
        return;
      }

      try {
        const layerIdsOnMap = new Set(mapLayersList.map(ml => ml.layer_id));
        const prevLayerIdsOnMap = new Set(prevMapLayersList.map(ml => ml.layer_id));
        const addedLayerIds = [...layerIdsOnMap].filter(id => !prevLayerIdsOnMap.has(id));

        // Detect initial hydration (server/client fetch) by any non-empty state
        if (
          !hasHydratedInitialData &&
          (mapLayersList.length > 0 || Object.keys(layers).length > 0)
        ) {
          hasHydratedInitialData = true;
        }

        if (isInitialLoad) {
          isInitialLoad = false;
          if (addedLayerIds.length === 0) {
            return;
          }
        }

        // Handle removals
        const removedLayerIds = [...prevLayerIdsOnMap].filter(id => !layerIdsOnMap.has(id));
        for (const layerId of removedLayerIds) {
          removeDataSource(layerId);
        }

        // Handle additions
        if (addedLayerIds.length > 0) {
          const layersToBuild = [];
          const layerIdsToFetch = [];

          for (const id of addedLayerIds) {
            if (layers[id]) {
              layersToBuild.push(layers[id]);
            } else {
              layerIdsToFetch.push(id);
            }
          }

          if (layerIdsToFetch.length > 0) {
            const { data: fetchedLayers, error } = await fetchLayersByIds(layerIdsToFetch);
            if (fetchedLayers && !error) {
              fetchedLayers.forEach(addLayer);
              layersToBuild.push(...fetchedLayers);
            } else {
              console.error('Failed to fetch newly added layers:', error);
            }
          }

          if (layersToBuild.length > 0) {
            const newSources = await buildLayerDataSources(layersToBuild, mapLayersList);
            newSources.forEach(addDataSource);
          }
        }

        // Handle updates for existing layers
        const existingLayerIds = [...layerIdsOnMap].filter(id => prevLayerIdsOnMap.has(id));
        for (const layerId of existingLayerIds) {
          const currentLayer = layers[layerId];
          const prevLayer = prevLayers[layerId];
          if (currentLayer && prevLayer && !isEqual(currentLayer, prevLayer)) {
            const statusChanged = currentLayer.status !== prevLayer.status;

            if (statusChanged) {
              if (currentLayer.status !== LAYER_STATUS.OK) {
                removeDataSource(layerId);
              } else {
                const updatedSources = await buildLayerDataSources([currentLayer], mapLayersList);
                updatedSources.forEach(addDataSource);
              }
            } else if (currentLayer.status === LAYER_STATUS.OK) {
              const updatedSources = await buildLayerDataSources([currentLayer], mapLayersList);
              updatedSources.forEach(addDataSource);
            }
          }
        }
      } catch (error) {
        console.error('Failed to process layer changes for data sources:', error);
      }
    },
    { equalityFn: isEqual }
  );

  return store;
};
